import React, {useEffect, useState} from 'react';
import MuiDrawer from '@mui/material/Drawer';
import {Button, TextField, Box, Typography} from '@mui/material';
import styled from 'styled-components';
import {AirplayOutlined, Close, PlayArrowOutlined, ArticleOutlined} from '@mui/icons-material'
import videoImg from "../../../../../../../assets/images/video.svg";
import { deleteButtonIcon } from "../../../../../../../icon/delete-button-preview";
import useVideoValidation from "../../../../../../../hooks/useVideoValidation";
import pdf from '../../../../../../../assets/icons/pdf.svg'
import useLectureValidation from "../../../../../../../hooks/useLectureValidation";
import {courseService} from "../../../../../../../services/course.service";
import {useFormik} from "formik";
import * as Yup from 'yup';


const StyledDrawer = styled(MuiDrawer).withConfig({ shouldForwardProp: (prop) => prop !== 'open' })<{open: boolean;}>`
  height: 100%;
  width: ${p => p.open ? 'auto' : `0`};
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1200;
  .MuiDrawer-paper {
    position: relative;
    transition: width 0.5s ease;
  }

  ${p => p.open && `
    &::before {
      content: '';
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(2px);
      z-index: -1;
    }
  `}
`;



const ChapterLectureDrawer = ({ activeDrawer, lecture, open, close, onSaveLecture, contentIndex }: {setActiveDrawer: (data: any) => void, activeDrawer: string, contentIndex: any, lecture?: any, open: boolean, onSaveLecture: (data: any) => void, close: (e?: string) => void, selectedChapterId: string}) => {
    const { validateVideo, ErrorModal: VideoErrorModal } = useVideoValidation();
    const { validateLecture, ErrorModal: LectureErrorModal } = useLectureValidation();
    const [type, setType] = useState( '');
    const [isUploading, setUploading] = useState(false);
    const baseValidationSchema = Yup.object({
        name: Yup.string()
            .trim(' ')
            .min(3, 'Минимальная длина названия лекции - 3 символа')
            .max(150, 'Максимальная длина названия лекции - 150 символов')
            .matches(/.*[a-zA-Zа-яА-Я]+.*/, 'Название лекции должно содержать хотя бы одну букву')
            .required('Сначала укажите название лекции'),
    });

    const articleValidationSchema = baseValidationSchema.concat(Yup.object({
        article: Yup.string()
            .trim(' ')
            .min(3, 'Минимальная длина названия статьи - 3 символа')
            .max(10000, 'Максимальная длина названия статьи - 10000 символов')
            .matches(/.*[a-zA-Zа-яА-Я]+.*/, 'В статье должно содержаться хотя бы одна буква')
            .required('Сначала укажите статью'),
    }));

    const videoValidationSchema = baseValidationSchema.concat(Yup.object({
        video: Yup.string().required('Сначала укажите видео'),
    }));

    const slidesValidationSchema = baseValidationSchema.concat(Yup.object({
        slides: Yup.string().required('Сначала укажите слайды'),
    }));

    const formik = useFormik({
        initialValues: {
            name: '',
            article: '',
            video: '',
            slides: '',
            slides_file_name: '',
            slides_file_extension: '',
            video_name: '',
            video_extension: '',
        },
        validationSchema: () => {
            switch(type) {
                case 'article':
                    return articleValidationSchema;
                case 'video':
                    return videoValidationSchema;
                case 'slide':
                    return slidesValidationSchema;
                default:
                    return baseValidationSchema;
            }
        },
        onSubmit: (values) => {
            onSaveLecture({
                lecture_name: values.name,
                article: values.article,
                video: values.video,
                video_name: values.video_name,
                video_extension: values.video_extension,
                slides: values.slides,
                slides_file_name: values.slides_file_name,
                slides_file_extension: values.slides_file_extension,
            });
            close();
        },
    });

    const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const errorMessage = await validateVideo(file);
            if (errorMessage) {
                return;
            }
            const formData = new FormData();
            formData.append('file', file);
            setUploading(true);
            const fullFileName = file.name;
            const fileNameParts = fullFileName.split('.');
            const fileType = fileNameParts.pop();
            const fileNameWithoutExtension = fileNameParts.join('.');

            try {
                const response = await courseService.loadVideo(formData);
                const videoURL = response.data.url;
                formik.setValues({
                    ...formik.values,
                    video: videoURL,
                    video_name: fileNameWithoutExtension,
                    video_extension: '.' + fileType,
                });
            } catch (error) {
                console.error('Ошибка при загрузке видео', error);
            } finally {
            setUploading(false);
        }
        }
    };
    const handleLectureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const errorMessage = await validateLecture(file);
            if (errorMessage) {
                console.error("Ошибка валидации файла:", errorMessage);
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64 = reader.result as string;
                const fullFileName = file.name;
                const fileNameParts = fullFileName.split('.');
                const fileType = fileNameParts.pop();
                const fileNameWithoutExtension = fileNameParts.join('.');
                try {

                    const response = await courseService.loadFile(base64, fullFileName, fileType);
                    const fileURL = response.data.image;
                    formik.setValues({
                        ...formik.values,
                        slides: fileURL,
                        slides_file_name: fileNameWithoutExtension,
                        slides_file_extension: '.' + fileType,
                    });
                } catch (error) {
                    console.error('Ошибка при загрузке файла', error);
                }
            };
        } else {
            console.error("Файл не выбран");
        }
    };
    const handleLectureDelete = async () => {
        try {
            const url = formik.values.slides;
            const video = url.split('/').pop();
            await courseService.deleteVideo(video);
            formik.setValues({ ...formik.values, slides: '' });
        } catch (error) {
            console.error('Ошибка при удалении:', error);
        }
    };
    const handleVideoDelete: any = async () => {
        try {
            const url = formik.values.video;
            const video = url.split('/').pop();
            await courseService.deleteVideo(video);
            formik.setValues({ ...formik.values, video: '' });
        } catch (error) {
            console.error('Ошибка при удалении:', error);
        }
    };

    useEffect(() => {
        if (activeDrawer === 'lecture' && lecture) {
            formik.setValues({
                name: lecture.lecture_name || '',
                article: lecture.article || '',
                video: lecture.video || '',
                slides: lecture.slides || '',
                slides_file_name: lecture.slides_file_name || '',
                slides_file_extension: lecture.slides_file_extension || '',
                video_name: lecture.video_name || '',
                video_extension: lecture.video_extension || '',
            });
            setType(lecture.article ? 'article' : lecture.video ? 'video' : lecture.slides ? 'slide' : '');

        } else {
            return
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return(
        <StyledDrawer variant="permanent"
                      anchor="right"
                      open={open}
                      component="form"
        >

            <div className="chapter-drawer-close" onClick={() => close()}><Close/></div>
            <div className="chapter-drawer-content">
                <div className="constructor-content-title">
                    Добавить лекцию
                </div>
                <div className="lecture-name">
                    Название лекции
                </div>
                <TextField variant="outlined"
                           placeholder="Введите название"
                           name="name"
                           onChange={formik.handleChange}
                           value={formik.values.name}
                           error={formik.touched.name && Boolean(formik.errors.name)}
                           helperText={formik.touched.name && formik.errors.name}
                           className="without-label"

                />
                <div className={formik.values.name.length > 150 ? "description-input-counter error-text" : "description-input-counter"}>{formik.values.name.length}/150</div>
                <div className="lecture-type-select-wrapper">
                    <div className="lecture-type-select-description">
                        Выберите и загрузите один из вариантов. Все файлы должны быть в качестве не ниже 720p и размером не более 4,0 ГБ.
                    </div>
                    {!type.length &&
                        <div className="lecture-type-select-controls">
                            <div className="lecture-type" onClick={() => setType('slide')}>
                                <div className="lecture-type-icon">
                                    <AirplayOutlined/>
                                </div>
                                <span>Слайды</span>
                            </div>
                            <div className="lecture-type" onClick={() => setType('video')}>
                                <div className="lecture-type-icon">
                                    <PlayArrowOutlined/>
                                </div>
                                <span>Видео</span>
                            </div>
                            <div className="lecture-type" onClick={() => setType('article')}>
                                <div className="lecture-type-icon">
                                    <ArticleOutlined/>
                                </div>
                                <span>Статья</span>
                            </div>
                        </div>
                    }
                    {type === 'article' &&
                        <>
                            <div className="article-field-label">
                                <div className="article-field-name">Статья</div>
                                <div className="select-other-type" onClick={() => setType('')}>выбрать другой вариант</div>
                            </div>
                            <TextField variant="outlined"
                                       placeholder="Введите текст"
                                       id="outlined-textarea"
                                       multiline
                                       name="article"
                                       onChange={formik.handleChange}
                                       value={formik.values.article}
                                       error={formik.touched.article && Boolean(formik.errors.article)}
                                       helperText={formik.touched.article && formik.errors.article}
                                       className="without-label"
                            />

                            <div className={formik.values.article.length > 10000 ? "description-input-counter error-text" : "description-input-counter"}>{formik.values.article.length}/10000</div>
                        </>
                    }
                    {type === 'video' &&
                        <>
                            <VideoErrorModal />
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#011224' }}>
                                        Видео
                                    </Typography>
                                    <Button variant="text" onClick={() => setType('')} sx={{ color: '#0573E4', fontSize: '14px' }}>
                                        выбрать другой вариант
                                    </Button>
                                </Box>
                                {isUploading && (
                                  <div className="loader-container">
                                      <p>Идет загрузка...</p>
                                  </div>
                                )}
                                {formik.values.video ? (
                                    <Box sx={{ position: 'relative' }}>
                                        <div className="video-preview">
                                            <video className='vid-preview' src={formik.values.video} controls/>
                                            <button className="delete-button" onClick={handleVideoDelete}>
                                                {deleteButtonIcon}
                                            </button>
                                        </div>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pt: '12px' }}>
                                        <div className={`file-select ${formik.touched.video && formik.errors.video ? 'file-select-error' : ''}`}>
                                            {!formik.values.video && <img src={videoImg} alt="default"/>}
                                            <label htmlFor="video">Загрузить видео</label>
                                            <input
                                                type="file"
                                                style={{ display: 'none' }}
                                                id="video"
                                                onChange={handleVideoUpload}
                                            />
                                        </div>
                                    </Box>
                                )}
                            </Box>
                        </>
                    }
                    {type === 'slide' &&
                        <>
                            <LectureErrorModal />
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#011224' }}>
                                        Слайды
                                    </Typography>
                                    <Button variant="text" onClick={() => setType('')} sx={{ color: '#0573E4', fontSize: '14px' }}>
                                        выбрать другой вариант
                                    </Button>
                                </Box>
                                <Box>
                                    {formik.values.slides ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '10px' }}>
                                            <img src={pdf} alt="PDFIcon"/>
                                            <Typography fontWeight={600} color={'black'} fontSize={16}>
                                                {formik.values.slides_file_name.length > 30
                                                    ? `${formik.values.slides_file_name.substring(0, 26)}...${formik.values.slides_file_name.slice(-4)}.pptx`
                                                    : `${formik.values.slides_file_name}${formik.values.slides_file_extension}`}
                                            </Typography>
                                            <Button variant="text" sx={{ color: '#0573E4', fontSize: '15px', textTransform: 'none'}} onClick={handleLectureDelete}>
                                                Удалить
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div className={`file-select ${formik.touched.slides && formik.errors.slides ? 'file-select-error' : ''}`}>
                                                {!formik.values.slides && <img style={{opacity: 0.7}} src={pdf} alt="default"/>}
                                                <label htmlFor="lecture">Загрузить лекцию</label>
                                                <input
                                                    type="file"
                                                    style={{ display: 'none' }}
                                                    id="lecture"
                                                    onChange={handleLectureUpload}
                                                />
                                            </div>
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                        </>
                    }
                </div>
            </div>
            {activeDrawer === 'lecture' && (
                <div className="chapter-drawer-footer-Lacture">
                    <Button
                        variant="contained"
                        onClick={(e) => {
                            e.preventDefault()
                            formik.handleSubmit();
                        }}>
                        Сохранить
                    </Button>
                </div>
            )}

        </StyledDrawer>
    );
}
export default ChapterLectureDrawer;
